import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, Loader2, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";

export default function Login() {
  const { user, demoLogin } = useAuth();
  const { branding } = useBranding();
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [demoBusy, setDemoBusy] = useState("");

  const LOGIN_IMG = branding.login_image || "https://images.unsplash.com/photo-1536520002442-39764a41e987?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";
  const headline = branding.login_headline || "Every chair.\nEvery store.\nEvery dollar.";
  const subtext = branding.login_subtext || "Turn weight-sensitive seat data into a live picture of your revenue — and back every unrecorded service with timestamped video.";

  if (user) return <Navigate to="/" replace />;

  const startDemo = async (role) => {
    setError("");
    setDemoBusy(role);
    const res = await demoLogin(role);
    setDemoBusy("");
    if (res.ok) navigate("/");
    else setError(res.error || "Demo is temporarily unavailable — please try again.");
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#1E2638]">
      {/* Left visual */}
      <div className="relative hidden lg:block overflow-hidden bg-[#211c16]">
        <img
          src={LOGIN_IMG}
          alt="salon"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-black/35" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-lg flex items-center justify-center bg-[#1E2638]/15 backdrop-blur">
              <ShieldCheck className="h-5 w-5 text-white" />
            </div>
            <div className="leading-tight">
              <span className="font-display font-semibold tracking-tight block">{branding.logo_text}</span>
              <span className="text-[10px] uppercase tracking-[0.2em] text-white/70">Revenue Assurance</span>
            </div>
          </div>
          <div>
            <h1 className="font-display text-4xl xl:text-5xl font-light tracking-tight leading-[1.1] whitespace-pre-line">
              {headline}
            </h1>
            <p className="mt-5 text-white/85 max-w-md leading-relaxed">
              {subtext}
            </p>
          </div>
          <div className="text-xs text-white/70">Seat-to-Sale Revenue Assurance · Video Protocol</div>
        </div>
      </div>

      {/* Right panel — demo entry only */}
      <div className="flex items-center justify-center p-6 bg-[#1E2638]">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-sm"
        >
          {/* Mobile-only salon hero (mirrors the chosen landing image) */}
          <div className="lg:hidden relative mb-6 h-36 w-full overflow-hidden rounded-xl" data-testid="mobile-login-hero">
            <img src={LOGIN_IMG} alt="salon" className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/20" />
            <div className="relative z-10 flex h-full flex-col justify-end p-4 text-white">
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-white/15 backdrop-blur">
                  <ShieldCheck className="h-4 w-4 text-white" />
                </div>
                <div className="leading-tight">
                  <span className="block font-display text-sm font-semibold tracking-tight">{branding.logo_text}</span>
                  <span className="text-[9px] uppercase tracking-[0.2em] text-[#38BDF8] font-semibold">Revenue Assurance</span>
                </div>
              </div>
            </div>
          </div>

          <h2 className="font-display text-2xl font-medium tracking-tight">Explore the platform</h2>
          <p className="mt-1.5 text-sm text-slate-400">No sign-up, no password. Choose a role and step straight into a live, fully working demo.</p>

          {error && <div className="mt-6 text-sm text-red-400" data-testid="login-error">{error}</div>}

          <div className="mt-8 space-y-3">
            <button
              onClick={() => startDemo("corporate")}
              disabled={!!demoBusy}
              data-testid="demo-corporate-btn"
              className="group w-full text-left rounded-xl border border-[#2A3550] bg-[#171E2E] px-5 py-4 hover:border-[#38BDF8] transition-colors duration-200 disabled:opacity-60 flex items-center justify-between gap-3"
            >
              <span>
                <span className="block font-medium">Corporate · HQ</span>
                <span className="text-xs text-slate-400">Network-wide view of every store</span>
              </span>
              {demoBusy === "corporate"
                ? <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                : <ArrowRight className="h-4 w-4 text-slate-500 group-hover:text-[#38BDF8] transition-colors duration-200" />}
            </button>
            <button
              onClick={() => startDemo("owner")}
              disabled={!!demoBusy}
              data-testid="demo-owner-btn"
              className="group w-full text-left rounded-xl border border-[#2A3550] bg-[#171E2E] px-5 py-4 hover:border-[#38BDF8] transition-colors duration-200 disabled:opacity-60 flex items-center justify-between gap-3"
            >
              <span>
                <span className="block font-medium">Store Owner</span>
                <span className="text-xs text-slate-400">A single owner's two stores</span>
              </span>
              {demoBusy === "owner"
                ? <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                : <ArrowRight className="h-4 w-4 text-slate-500 group-hover:text-[#38BDF8] transition-colors duration-200" />}
            </button>
          </div>

          <p className="mt-6 text-xs text-slate-500">A live simulation on demonstration data — no real customer records.</p>
        </motion.div>
      </div>
    </div>
  );
}
